import React, { useState, useEffect } from 'react';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import Autocomplete from '../../components/SimpleAutocomplete';
import HTTPError from '@scale/frontend-lib/httpError';
import { AutocompleteOption } from 'design-system/Autocomplete/Autocomplete';
import { useOCPStore } from '../hooks/useOCPStore';
import { useDraftFlowStore } from '../hooks/useDraftFlowStore';
import PanelEntry from './PanelEntry';
import {
  OnboardingStepType,
  OnboardingFlowStepNode,
  SkillScreeningStepConfig,
  OnboardingFlowNode,
} from '@scale/shared/onboardingControlPanel';
import {
  ListScreeningConfigsResponse,
  Person,
  ScreeningConfig,
  ScreeningManagement,
  ScreeningQuestionBankStatus,
  ScreeningQuestionDifficulty,
} from '@scale/shared-types';
import { Icon, Typography, SelectMenu } from '@scale/scaleui';
import CopyToClipboard from 'react-copy-to-clipboard';
import { isNodeUneditable } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/config/config';

const SkillScreeningConfigAutocomplete: React.FC<{
  required?: boolean;
  stepType: OnboardingStepType;
}> = ({ required, stepType }) => {
  const { currentFlow, currentVersion, detailTarget, setDetailTarget, canFreeEdit } = useOCPStore(
    state => ({
      currentFlow: state.currentFlow,
      currentVersion: state.currentVersion,
      detailTarget: state.detailTarget,
      setDetailTarget: state.setDetailTarget,
      canFreeEdit: state.canFreeEdit,
    }),
  );
  const editDraftEntityValue = useDraftFlowStore(state => state.editDraftEntityValue);
  const [screeningConfigId, setScreeningConfigId] = useState<string>(
    ((detailTarget as OnboardingFlowStepNode)?.config as SkillScreeningStepConfig)
      ?.screeningConfigId ?? '',
  );
  const [screeningConfigs, setScreeningConfigs] = useState<
    Map<string, ScreeningConfig<Person>> | undefined
  >();
  const [fixedDifficulty, setFixedDifficulty] = useState<ScreeningQuestionDifficulty | undefined>(
    ((detailTarget as OnboardingFlowStepNode)?.config as SkillScreeningStepConfig)?.fixedDifficulty,
  );
  const [searchText, setSearchText] = useState<string>('');
  const uneditable =
    currentVersion !== -1 ||
    isNodeUneditable(detailTarget as OnboardingFlowNode, canFreeEdit ? currentFlow : null);
  const [options, setOptions] = useState<AutocompleteOption[]>([]);

  useEffect(() => {
    if (currentFlow && detailTarget) {
      const newDetailTarget = editDraftEntityValue(currentFlow._id, detailTarget, {
        ...detailTarget,
        config: {
          ...((detailTarget as OnboardingFlowStepNode).config ?? {}),
          type: stepType,
          screeningConfigId,
          fixedDifficulty,
        },
      });
      setDetailTarget(newDetailTarget);
    }
  }, [screeningConfigId, fixedDifficulty]);

  const handleSelectOption = (option: AutocompleteOption) => {
    setScreeningConfigId(option.id);
    setSearchText('');
    setFixedDifficulty(undefined);
  };

  const handleInputChange = (text: string) => {
    setSearchText(text);
  };

  useEffect(() => {
    const fetchScreeningConfigs = async () => {
      const configs: ListScreeningConfigsResponse = await apiFetch(
        null,
        `corp-api/screening-center/config`,
        {
          method: 'GET',
        },
      );
      setScreeningConfigs(new Map(configs.map(config => [config._id, config])));
      const readyConfigs = configs.filter(
        config => config.status !== ScreeningQuestionBankStatus.InProgress,
      );
      const newOptions = readyConfigs.map(config => ({
        id: config._id,
        title: config.title,
      })) as AutocompleteOption[];

      setOptions(newOptions);

      const screeningConfigId =
        ((detailTarget as OnboardingFlowStepNode)?.config as SkillScreeningStepConfig)
          ?.screeningConfigId ?? '';

      if (screeningConfigId && newOptions.some(option => option.id === screeningConfigId)) {
        setScreeningConfigId(screeningConfigId);
      } else {
        setScreeningConfigId('');
      }
    };

    fetchScreeningConfigs().catch(error => {
      defaultApiFetchErrorHandler(error as HTTPError, { handleHttp: true });
      setOptions([]);
    });
  }, [detailTarget]);

  const filteredOptions = searchText
    ? options.filter(
        option =>
          option.title.toLowerCase().includes(searchText.toLowerCase()) ||
          option.id.toLowerCase().startsWith(searchText.toLowerCase()),
      )
    : options;

  return (
    <PanelEntry title={`Screening${required ? '*' : ''}`}>
      {!!options.length && screeningConfigId && (
        <Typography.Paragraph3 className="ml-1">
          Skill Screening Name: {options.find(option => option.id === screeningConfigId)?.title}
        </Typography.Paragraph3>
      )}
      <div className="flex gap-1 items-center">
        {screeningConfigId && (
          <>
            <Typography.Paragraph3 className="ml-1">
              Screening ID: {screeningConfigId}
            </Typography.Paragraph3>
            <CopyToClipboard text={screeningConfigId}>
              <Icon icon="copy" variant="neutral" size="sm" />
            </CopyToClipboard>
          </>
        )}
      </div>
      <Autocomplete
        className="w-full"
        options={filteredOptions}
        value={{ id: screeningConfigId, title: searchText }}
        setSelectedOption={handleSelectOption}
        onInputChange={handleInputChange}
        disabled={uneditable}
        placeholder="Search screening configs by name or ID..."
      />
      {screeningConfigs?.get(screeningConfigId)?.screeningManagement ===
        ScreeningManagement.Automated && (
        <div className="mt-2">
          <Typography.Paragraph3 className="ml-1 mb-1">Difficulty</Typography.Paragraph3>
          <SelectMenu
            className="w-full"
            options={[
              { id: ScreeningQuestionDifficulty.Unspecified, name: 'Adaptive Difficulty' },
              { id: ScreeningQuestionDifficulty.Easy, name: 'Easy' },
              { id: ScreeningQuestionDifficulty.Medium, name: 'Medium' },
              { id: ScreeningQuestionDifficulty.Hard, name: 'Hard' },
            ]}
            selectedOptionId={fixedDifficulty ?? ScreeningQuestionDifficulty.Unspecified}
            setSelected={(id: string) =>
              setFixedDifficulty(
                id === ScreeningQuestionDifficulty.Unspecified
                  ? undefined
                  : (id as ScreeningQuestionDifficulty),
              )
            }
            disabled={
              uneditable ||
              screeningConfigs?.get(screeningConfigId)?.screeningManagement !==
                ScreeningManagement.Automated
            }
          />
        </div>
      )}
    </PanelEntry>
  );
};
export default SkillScreeningConfigAutocomplete;
